import React, { useState } from 'react';
import '../Styles/Login.css'; // import the specific stylesheet for this page;
import { useNavigate } from 'react-router-dom';
import logo from '../assets/GMF logo no background.png';

function Login() {
    const [username, setUsername] = useState(''); // username is actually an email
    const [password, setPassword] = useState('');

    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState('');
    const [role, setRole] = useState('GMF_EMPLOYEE');

    const [maxAttempts] = useState(8); // Max attempts before account lock
    // the message to show to the user (either their attempts left, or that they're locked out)
    const [attemptsMessage, setAttemptsMessage] = useState('');
    const [showAttemptsMessage, setShowAttemptsMessage] = useState(false);

    // whether or not this account is locked out because of too many incorrect attempts
    const [isLockedOut, setIsLockedOut] = useState(false);

    const navigate = useNavigate();
    const url = process.env.REACT_APP_API_URL;

    const validateForm = () => {
        // Reset errors
        setEmailError('');
        setPasswordError('');
        setError(null);

        // Basic email validation
        if (!username.includes('@')) {
            setEmailError('Please enter a valid email.');
            return false;
        }

        // Password validation
        if (password.length < 6) {
            setPasswordError('Password must be at least 6 characters long.');
            return false;
        }

        setError('');
        return true;
    };

    // Function to get failed attempts for a specific email
    const getFailedAttempts = (email) => {
        const storedAttempts = sessionStorage.getItem(email);
        return storedAttempts ? parseInt(storedAttempts) : 0;
    };

    // update the value of the number of failed login attempts for a specific email
    const updateFailedAttempts = (email) => {
        const currentAttempts = getFailedAttempts(email);
        const newAttempts = currentAttempts + 1;
        sessionStorage.setItem(email, newAttempts.toString());
        return newAttempts;
    };

    // get rid of the stored number of failed login attempts for a specific email
    // this is for if the user logs in successfully, and the next time they try to login we want them to have 5 fresh attempts
    const resetFailedAttempts = (email) => {
        sessionStorage.removeItem(email);
    };

    const handleIncorrectAttempt = (username) => {
        // increment the number of failed attempts for this email (username)
        updateFailedAttempts(username);

        updateMessageAndLockedOut(username);
    };

    const updateMessageAndLockedOut = (newEmail) => {
        // reset the value of isLockedOut and showAttemptsMessage
        setIsLockedOut(false);
        setShowAttemptsMessage(false);

        let failedAttempts = getFailedAttempts(newEmail);

        // no failed attempts on this email yet
        if (failedAttempts === 0) {
            // do nothing more
        }
        // at least 1 failed attempt, but they're not locked out yet
        else if (failedAttempts < maxAttempts) {
            // don't lock them out but show the message
            setAttemptsMessage(
                'You have ' +
                    (maxAttempts - failedAttempts) +
                    ' incorrect attempts left before your account gets locked.'
            );

            setShowAttemptsMessage(true);
        }
        // too many failed attempts - lock them out
        else {
            // give them a pop-up, show the message, and lock them out
            alert('Your account has been locked. Please reset your password.');
            setIsLockedOut(true);
            setAttemptsMessage(
                'Your account has been locked. Please reset your password.'
            );
            setShowAttemptsMessage(true);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        // ensures form is filled
        if (!validateForm()) return;
        setLoading(true);

        const formDetails = new URLSearchParams();
        formDetails.append('username', username);
        formDetails.append('password', password);

        const endpoint =
            role === 'GMF_EMPLOYEE'
                ? `${url}/gmf-employee/login`
                : `${url}//institution-account/login`;
        try {
            // depends on the role variable, call different endpoints
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: username,
                    password
                }),
            });

            setLoading(false);

            if (response.ok) {
                // store response token when response is received
                const data = await response.json();

                // If login is successful, clear failed attempts for this email address
                resetFailedAttempts(username);

                localStorage.setItem('token', data.token);
                localStorage.setItem('user', JSON.stringify(data));
                localStorage.setItem(
                    'role',
                    role === 'GMF_EMPLOYEE' ? 'GMF_EMPLOYEE' : 'INSTITUTION'
                ); // defaults to GMF_EMPLOYEE if no user type provided
                // TODO: talk to backend guys about providing user-type in the LoginResponse

                // output for testing
                console.log(localStorage.getItem('token'));

                navigate('/gmfhome', { replace: true });

                // // Redirects user to homepage for their role
                // if (data.user_type === 'GMF_EMPLOYEE') {
                //     navigate('/gmfhome', { replace: true });
                // } else {
                //     navigate('/protected', { replace: true });
                // }
            } else {
                const errorData = await response.json();
                setError(errorData.detail || 'Authentication failed!');

                handleIncorrectAttempt(username);
            }
        } catch (error) {
            setLoading(false);
            setError('An error occurred. Please try again later.');
        }
    };

    return (
        <div className="login-container">
            <div className="logo-container">
                <img className="logo" src={logo} alt="MD-Sensei Logo"></img>
            </div>

            <div className="login-box">
                <h1>Welcome to the MD-Sensei Admin Platform</h1>

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Login as:</label>
                        <select
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                        >
                            <option value="GMF_EMPLOYEE">GMF Employee</option>
                            <option value="INSTITUTION">Institution</option>
                        </select>
                        <label>Email:</label>
                        <input
                            type="email"
                            value={username}
                            onChange={(e) => {
                                const newEmail = e.target.value;
                                setUsername(newEmail);

                                // update isLockedOut and the attempts message to the user for this new email
                                updateMessageAndLockedOut(newEmail);
                            }}
                            className={emailError ? 'error-input' : ''}
                        />
                        {emailError && (
                            <span className="error-message">{emailError}</span>
                        )}
                    </div>

                    <div className="form-group">
                        <label>Password:</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className={passwordError ? 'error-input' : ''}
                        />
                        {passwordError && (
                            <span className="error-message">
                                {passwordError}
                            </span>
                        )}
                    </div>

                    <div>
                        <button type="submit" disabled={loading || isLockedOut}>
                            {loading ? 'Logging in...' : 'Login'}
                        </button>
                        {error && <p className="error-message">{error}</p>}
                    </div>

                    <p className="password-help">
                        Forgot your password? You can{' '}
                        <a href="/resetpassword">reset it</a>.
                    </p>

                    <p className="create-account">
                        Don't have an account yet?{' '}
                        <a href="/create-gmf-account">Make one</a>!
                    </p>

                    {/* add the error-text class when the user is locked out */}
                    {showAttemptsMessage && (
                        <p
                            className={isLockedOut ? 'error-text' : ''}
                            id="attempts-message"
                        >
                            {attemptsMessage}
                        </p>
                    )}
                </form>
            </div>
        </div>
    );
}

export default Login;
